import React, { useContext, useEffect, useState } from "react";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  MenuItem,
  TextField,
  Typography,
  Checkbox,
  FormControl,
  FormControlLabel,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "../../../contexts/InfoContext";
import { useAuth } from "../../../contexts/AuthContext";
import fetchData from "../../../api/services/Users/getUser";
import getInternationalDate from "../../../utils/getDate";
import { SpinnerContext } from "../../../contexts/SpinnerContext";
import CustomTextField from "../../VersatileComponents/orderTextInput";
import getRequiredUserData from "../../../utils/getBranchInfo";
import create from "../../../api/orders/create";
import useUserClaims from "../../../hooks/useUserClaims";
import capitalizeString from "../../../utils/capitalizeString";
import checkCustomerBlock from "../../../api/customer/checkCustomerBlock"; // Added import

// Validation schema
const WaterOrderFormValidationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  blockHouse: Yup.string().required("Block House is required"),
  phone: Yup.string().required("Phone is required"),
  customerKey: Yup.string().required("Customer Key is required"),
  billPayerName: Yup.string().required("Bill Payer Name is required"),
  deliveryguyId: Yup.string().required("Please select a Delivery Guy"),
  branchId: Yup.string().required("Please select a Branch"),
  customerType: Yup.string().required("Customer Type is required"),
  price: Yup.number().when("customerType", {
    is: (value) => value === "negotiable",
    then: () =>
      Yup.number()
        .required("Price is required for negotiable orders")
        .positive("Price must be positive"),
    otherwise: () => Yup.number().nullable(),
  }),
});

const DEBOUNCE_DELAY = 1000; // Added constant for debounce delay

const WaterOrderBranchForm = () => {
  const [showForm, setShowForm] = useState(false);
  const { openSnackbar } = useSnackbar();
  const { user } = useAuth();
  const theme = useTheme();
  const { setIsSubmitting } = useContext(SpinnerContext);
  const [deliveryGuy, setDeliveryGuy] = useState([]);
  const [customerType, setCustomerType] = useState("regular");
  const [isBlocked, setIsBlocked] = useState(false); // Added state for block status
  const userData = getRequiredUserData();
  const userClaims = useUserClaims(user);
  useEffect(() => {
    const unsubscribe = fetchData("Deliveryturn", setDeliveryGuy);
    return () => unsubscribe();
  }, []);

  const handleDeliveryGuyChange = (event) => {
    const Id = event.target.value;
    const Name = deliveryman.find((employee) => employee[1] === Id)?.[0] || "";
    formik.setFieldValue("deliveryguyName", Name);
    formik.setFieldValue("deliveryguyId", Id);
  };

  const handleNegotiableChange = (event) => {
    const isChecked = event.target.checked;
    if (!isBlocked) {
      // Check isBlocked
      const newType = isChecked ? "negotiable" : "regular";
      setCustomerType(newType);
      formik.setFieldValue("customerType", newType);
      if (!isChecked) {
        formik.setFieldValue("price", "");
      }
    }
  };

  const handleButtonClick = () => {
    if (userData.activeTable) {
      setShowForm(true);
    } else {
      openSnackbar(
        `${userData.branchName} branch does not have a daily table today or sheet. So you can't create an order. Please create a sheet or table.`,
        "info"
      );
    }
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      phone: "",
      blockHouse: "",
      billPayerName: "",
      customerKey: "",
      deliveryguyName: "",
      branchName: userData.branchName,
      branchId: userData.requiredId,
      deliveryguyId: "",
      activeTable: userData.activeTable,
      active: userData.active,
      activeDailySummery: userData.activeDailySummery,
      customerType: "regular",
      price: "",
      deliveryType: "regular",
    },
    validationSchema: WaterOrderFormValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const date = getInternationalDate();
        const name = capitalizeString(values.name);

        if (!values.deliveryguyId || !values.deliveryguyName) {
          handleCloseForm();
          throw {
            response: {
              data: {
                message:
                  "Delivery guy information is not found. Please check your connection, refresh your browser, and try again.",
                type: "error",
              },
            },
          };
        }

        if (
          !values.branchId ||
          !values.branchName ||
          !values.activeTable ||
          !values.active ||
          !values.activeDailySummery
        ) {
          handleCloseForm();
          throw {
            response: {
              data: {
                message:
                  "Branch information is not found. Please check your connection, refresh your browser, and try again.",
                type: "error",
              },
            },
          };
        }

        values.name = name;
        values.date = date;
        values.callcenterId = userData.requiredId || user.displayName;
        values.customerType = customerType;
        values.status = "Assigned";
        values.branchKey = values.callcenterId;
        values.blockHouse = values.blockHouse.toUpperCase();
        values.from = "branch";

        if (values.customerType === "negotiable" && values.price) {
          values.price = parseFloat(values.price);
        } else {
          delete values.price;
        }

        values.deliveryType = "regular";

        const res = await create(user, values, "water");
        openSnackbar(`${res.data.message}!`, "success");
        handleCloseForm();
      } catch (error) {
        if (error.response && error.response.data) {
          openSnackbar(
            error.response.data.message,
            error.response.data.type ? error.response.data.type : "error"
          );
        } else {
          openSnackbar(
            "An unexpected error occurred.Please kindly check your connection.",
            "error"
          );
        }
      }
      setIsSubmitting(false);
    },
  });

  // Debounced effect for checking customer block status
  useEffect(() => {
    const handler = setTimeout(() => {
      const checkCustomer = async () => {
        if (!formik.values.blockHouse) {
          setCustomerType("regular");
          setIsBlocked(false);
          formik.setFieldValue("customerType", "regular");
          return;
        }

        try {
          const isBlocked = await checkCustomerBlock(formik.values.blockHouse);
          setIsBlocked(isBlocked);
          if (isBlocked) {
            openSnackbar(
              "This customer is blocked and cannot place orders.",
              "error"
            );
            return;
          }

          setCustomerType("regular");
          formik.setFieldValue("customerType", "regular");
        } catch (error) {
          console.error("Error checking customer:", error);
          openSnackbar("Failed to check customer status.", "error");
          setCustomerType("regular");
          setIsBlocked(false);
          formik.setFieldValue("customerType", "regular");
        }
      };

      checkCustomer();
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [formik.values.blockHouse]);

  const handleCloseForm = () => {
    setShowForm(false);
    formik.resetForm();
    setCustomerType("regular");
    setIsBlocked(false); // Reset block status
  };

  const deliveryMan = deliveryGuy
    ? deliveryGuy[formik.values.branchId ? formik.values.branchId : ""]
    : [];
  const deliveryman = deliveryMan?.map((item) => [
    item.deliveryGuyName,
    item.deliveryManId,
  ]);

  useEffect(() => {
    const { branchId, activeTable, branchName } = formik.values;
    if (branchId && !activeTable) {
      openSnackbar(
        `${branchName} branch does not have a daily table today or sheet. So you can't create an order. Please create Sheet or Table.`,
        "info"
      );
    }
  }, [formik.values]);

  return (
    <div>
      {userClaims.admin ? (
        <Button variant="contained" color="primary" onClick={handleButtonClick}>
          Create new Water Order
        </Button>
      ) : null}

      <Dialog open={showForm} onClose={handleCloseForm} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: theme.palette.background.alt }}>
          New Water Order
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: theme.palette.background.alt }}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              {/* Name */}
              <CustomTextField
                name="name"
                label="Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.name && formik.touched.name}
                helperText={
                  formik.errors.name && formik.touched.name
                    ? formik.errors.name
                    : ""
                }
                touched={formik.touched.name}
              />

              {/* Phone */}
              <CustomTextField
                name="phone"
                label="Phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.phone && formik.touched.phone}
                helperText={
                  formik.errors.phone && formik.touched.phone
                    ? formik.errors.phone
                    : ""
                }
                touched={formik.touched.phone}
              />

              {/* blockHouse */}
              <CustomTextField
                name="blockHouse"
                label="blockHouse"
                value={formik.values.blockHouse}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.blockHouse && formik.touched.blockHouse}
                helperText={
                  formik.errors.blockHouse && formik.touched.blockHouse
                    ? formik.errors.blockHouse
                    : ""
                }
                touched={formik.touched.blockHouse}
              />

              {/* Customer Type and Block Status Display */}
              <Grid item xs={12}>
                <Typography variant="subtitle1">
                  Customer Type:{" "}
                  {customerType.charAt(0).toUpperCase() + customerType.slice(1)}
                </Typography>
                {isBlocked && (
                  <Typography variant="subtitle1" color="error">
                    Customer is blocked and cannot place orders.
                  </Typography>
                )}
              </Grid>

              {/* Negotiable Checkbox */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customerType === "negotiable"}
                      onChange={handleNegotiableChange}
                      disabled={isBlocked} // Disable if blocked
                      color="primary"
                    />
                  }
                  label="Negotiable Order"
                />
              </Grid>

              {/* Price (for Negotiable) */}
              {customerType === "negotiable" && (
                <CustomTextField
                  name="price"
                  label="Custom Price"
                  type="number"
                  value={formik.values.price}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.errors.price && formik.touched.price}
                  helperText={
                    formik.errors.price && formik.touched.price
                      ? formik.errors.price
                      : ""
                  }
                  touched={formik.touched.price}
                  disabled={isBlocked} // Disable if blocked
                />
              )}

              {/* customerKey */}
              <CustomTextField
                name="customerKey"
                label="customerKey"
                value={formik.values.customerKey}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.customerKey && formik.touched.customerKey}
                helperText={
                  formik.errors.customerKey && formik.touched.customerKey
                    ? formik.errors.customerKey
                    : ""
                }
                touched={formik.touched.customerKey}
                disabled={isBlocked} // Disable if blocked
              />

              <CustomTextField
                name="billPayerName"
                label="billPayerName"
                value={formik.values.billPayerName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.errors.billPayerName && formik.touched.billPayerName
                }
                helperText={
                  formik.errors.billPayerName && formik.touched.billPayerName
                    ? formik.errors.billPayerName
                    : ""
                }
                touched={formik.touched.billPayerName}
                disabled={isBlocked} // Disable if blocked
              />

              <Grid item xs={12} sm={6} md={6} lg={6}>
                <TextField
                  select
                  name="deliveryguyId"
                  variant="outlined"
                  fullWidth
                  required
                  value={formik.values.deliveryguyId}
                  onChange={handleDeliveryGuyChange}
                  onBlur={formik.handleBlur}
                  label="Delivery Guy"
                  disabled={isBlocked} // Disable if blocked
                  InputLabelProps={{
                    shrink: true,
                    style: {
                      marginTop: "8px",
                      color: theme.palette.secondary[100],
                    },
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          backgroundColor: theme.palette.background.alt,
                          color: "inherit",
                        },
                      },
                    },
                  }}
                  error={
                    formik.touched.deliveryguyId &&
                    Boolean(formik.errors.deliveryguyId)
                  }
                  helperText={
                    formik.touched.deliveryguyId && formik.errors.deliveryguyId
                  }
                >
                  <MenuItem value="">Select Delivery Guy</MenuItem>
                  {deliveryman?.map((branch) => (
                    <MenuItem key={branch[1]} value={branch[1]}>
                      {branch[0]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid
                item
                xs={12}
                container
                alignItems="end"
                justifyContent="end"
              >
                <DialogActions
                  sx={{ backgroundColor: theme.palette.background.alt }}
                >
                  <Button
                    variant="contained"
                    onClick={handleCloseForm}
                    color="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className="submit-button"
                    disabled={isBlocked || formik.isSubmitting} // Disable if blocked
                  >
                    Submit
                  </Button>
                </DialogActions>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WaterOrderBranchForm;
